<template>
  <CardSection :loading="isSavableStateLoading" :error="majorError">
    <template #header>
      Manage Checklist Templates
    </template>

    <template #body>
      <div class="form-group row">
        <div class="form-group mr-2 mb-0 col-12 d-flex">
          <router-link :to="{ name: 'admin_checklist_templates_new' }" class="btn btn-primary btn-sm ml-auto">
            Add New Checklist Template
          </router-link>
        </div>
      </div>
      <PortalTable ref="template_index" table-id="template_index"
        :table-data="records"
        :total-records="totalRecords"
        :page="pageNumber"
        :page-count="pageCount"
        :loading="isSavableStateProcessing"
        :filter-values="filterValues"
        filter-display="row"
        empty="No Templates found!"
        @on-sort="handleSortChange"
        @on-page="handlePageChange"
        @on-filter-change="onFilterChange">
        <template #columns>
          <Column field="templateCategoryNames" 
            header="Category Name" 
            filter-field="template_category_ids" 
            :show-filter-menu="false">
            <template #filter="{ filterModel = { value: null }, filterCallback }">
              <MultiSelectFilter ref="template_category_ids" 
                filter-field="template_category_ids"
                option-value="id"
                option-label="category_name"
                :options="checklistStore.categories"
                @change="onChange" />
            </template>
          </Column>
          <Column field="template_name" 
            header="Template Name" 
            sort-field="template_name"
            :sortable="true"
            filter-field="template_name" 
            :show-filter-menu="false">
            <template #filter="{ filterModel = { value: null }, filterCallback }">
              <InputText v-model="filterModel.value"
                type="text"
                class="p-column-filter"
                placeholder="Search by Template Name"
                @input="filterCallback()" />
            </template>
          </Column>
          <Column field="transplant_center.code" 
            header="TxC" 
            :sortable="true"
            filter-field="tc_id"
            :show-filter-menu="false"
            :filter-menu-style="{ width: '15rem' }"
            style="min-width: 14rem">
            <template #filter>
              <MultiSelectFilter ref="tc_id" 
                filter-field="tc_id"
                :options="checklistStore.transplantCenters" 
                @change="onChange" />
            </template>
          </Column>   
          <Column field="organ_group.name" 
            header="Organ"
            :sortable="true"
            filter-field="organ_group_id"
            :show-filter-menu="false"
            :filter-menu-style="{ width: '14rem' }"
            style="min-width: 14rem">
            <template #filter>
              <MultiSelectFilter ref="organ_group_id" 
                filter-field="organ_group_id"
                option-label="name"
                :options="checklistStore.organGroups"
                @change="onChange" />
            </template>
          </Column>
          <Column field="created_by.full_name" 
            header="Created By" 
            :sortable="true" />
          <Column field="updated_by.full_name" 
            header="Last Edited By" />
          <Column field="disabled_at" 
            header="State"
            filter-field="state"
            :show-filter-menu="false"
            :filter-menu-style="{ width: '14rem' }"
            style="min-width: 14rem">
            <template #filter>
              <MultiSelectFilter ref="state" 
                filter-field="state"
                option-value="code"
                cache-context-key="state"
                class="form-control form-control-select"
                option-label="name"
                :options="stateOptions"
                @change="onChange" />
            </template>
            <template #body="slotProps">
              {{ slotProps.data.disabled_at ? 'Disabled' : 'Enabled' }}
            </template>
          </Column>
          <Column header="Actions" class="btn-2-icon-group">
            <template #body="slotProps">
              <div class="d-flex">
                <router-link :to="{ name: 'admin_checklist_templates_edit', params: { id: slotProps.data.id, createNew: 'true' } }"
                  class="action btn btn-fill btn-outline-primary mx-2">
                  Edit
                </router-link>
                <router-link v-if="!slotProps.data.disabled_at" :to="{ name: 'admin_checklist_templates_new', query: { duplicate_from: slotProps.data.id } }"
                  class="action btn btn-fill btn-outline-primary mx-2">
                  <font-awesome-icon :icon="['fas', 'fa-copy']" aria-hidden="true" />
                </router-link>
                <button v-else
                  disabled
                  class="action btn btn-fill btn-outline-primary mx-2"
                  title="Cannot duplicate: Templates is disabled.">
                  <font-awesome-icon :icon="['fas', 'fa-copy']" aria-hidden="true" />
                </button>
              </div>
            </template>
          </Column>
        </template>
      </PortalTable>
    </template>
  </CardSection>
</template>

<script lang="ts">
import CardSection from "@/src/components/shared/CardSection.vue";
import PortalTable from "@/src/components/shared/PortalTable.vue";
import Column from 'primevue/column';
import { savableStateMixin } from "@/src/mixins/savableStateMixin";
import { pageMixin } from "@/src/mixins/pageMixin";
import { paginationMixin } from "@/src/mixins/paginationMixin";
import InputText from 'primevue/inputtext';
import { FilterMatchMode } from '@primevue/core/api';
import MultiSelectFilter from "@/src/components/shared/FormComponents/MultiSelectFilter.vue";
import { useChecklistStore } from '@/src/stores/checklist';
import { Template } from "@/src/models/match/checklist/template";
import { TemplateCategory } from "@/src/models/match/checklist/template-category";


export default {
  components: {
    CardSection,
    PortalTable,
    Column,
    InputText,
    MultiSelectFilter
  },
  mixins: [ savableStateMixin, pageMixin, paginationMixin ],
  setup() {
    const checklistStore = useChecklistStore();
    return { checklistStore };
  },
  data() {
    return {
      filterValues: {
        "template_category_ids": { value: null, matchMode: FilterMatchMode.STARTS_WITH },
        "tc_id" : { value: null, matchMode: FilterMatchMode.STARTS_WITH },
        "organ_group_id" : { value: null, matchMode: FilterMatchMode.STARTS_WITH },
        "template_name": { value: null, matchMode: FilterMatchMode.STARTS_WITH },
        "state" : { value: ['enabled'], matchMode: FilterMatchMode.IN }
      },
      stateOptions: [
        { code: "enabled", name: "Enabled" },
        { code: "disabled", name: "Disabled" }
      ]
    }
  },
  async mounted() {
    try {
      this.setSavableStateLoading();
      await this.checklistStore.loadTransplantCentres();
      await this.checklistStore.loadOrganGroups();
      await this.checklistStore.loadCategories();
      window.localStorage.setItem('multiselectfilter.state', 'enabled');

      await this.getRowData();
      this.setSavableStateIdle();
    } catch(error) {
      this.handleMajorError(error);;
    }
  },
  methods: {
    async getRowData(reset = false) {
      this.setSavableStateProcessing();
      if (reset) this.pageNumber = 1;

      let params = {
        ...this.search_params,
        page: this.pageNumber,
        sort_by: this.currentSortField,
        sort_type: this.currentSortType,
      };

      try {
        const response = await this.checklistStore.loadTemplates(params);

        this.processPagyResponse(response, this.processIncomingData);
       
        this.setSavableStateIdle();
      } catch(error) {
        this.handleMajorError(error);
      }
    },
    processIncomingData(data: any[]) {
      const parsedRecords: Template[] = [];
      data.forEach(r => parsedRecords.push(Template.buildFromAPI(r)));

      return parsedRecords;
    },
    onFilterChange(event: any) {
      if(event["tc_id"] === "") this.$refs.tc_id.clearFilters();
      if(event["organ_group_id"] === "") this.$refs.organ_group_id.clearFilters();

      this.search_params = event;
      this.getRowData(true);
    },
    onChange(event) {
      this.$refs.template_index.resetFilters(event);
    },
    translateCategories(records: TemplateCategory[]): String {
      if(!records) return '';

      return records.map((item) => {
        return item.category_name;
      }).join(', ')
    },
  }
}
</script>
